import { GradientDefinition, TableTemplateItem, TemplateItem } from "types";
import { hexToRgba, rgbaToHex } from "utils";
import { sanitizeText } from "utils/text";
import {
  AnimationEffects,
  AnimationConfig,
  CopyPasteItem,
  CustomText,
  Effects,
  FadeMethod,
  ItemType,
  ItemWithLayerIndex,
  LINE_ORIENTATIONS,
  LineOrientation,
  MoveMethod,
  MediaItem,
  MainImage,
  MediaKind,
  Avatar,
  Shape,
  ShapeType,
  TemplateItemWithLayerIndex,
  TemplateState,
  TemplateItemWithId,
  AvatarShape,
  SLIDE_LAYOUT_SHAPE_NAMES,
  SLIDE_LAYOUT_SHAPE_PARTIAL_NAMES,
  Figure,
  ParameteredText,
  HorizontalTextAlign,
  Slot,
  TemplateType,
  VideoScene,
  ITEM_TYPES,
} from "../types";
import {
  DEFAULT_TITLE_CONTENT,
  NULL_COLOR,
  TEXT_FONT_PLACEHOLDER,
  SUBTITLES_FONT_PLACEHOLDER,
  TITLE_FONT_PLACEHOLDER,
  SECONDARY_COLOR_PLACEHOLDER,
  PRIMARY_COLOR_PLACEHOLDER,
  TITLE_COLOR_PLACEHOLDER,
  SUBTITLE_COLOR_PLACEHOLDER,
  DEFAULT_TEXT_COLOR,
  INTRODUCTION_VIDEO_PLACEHOLDER,
  CLOSING_VIDEO_PLACEHOLDER,
  MAX_INTRODUCTION_DURATION_IN_FRAMES,
  INTRODUCTION_DURATION_PLACEHOLDER,
  CLOSING_DURATION_PLACEHOLDER,
  MAX_CLOSING_DURATION_IN_FRAMES,
  MAX_TRANSITION_DURATION_IN_FRAMES,
  DEFAULT_TRANSITION_DURATION_IN_FRAMES,
  VIDEO_FPS,
} from "../constants";
import { nanoid } from "nanoid";
import { generateAvatarItem } from "./generators/items/Avatar";
import { generateAvatarShapeItem } from "./mapTemplateStateToServerTemplate";
import { components } from "@libertify/api-types/media";
import {
  BrandKit,
  BrandKitAsset,
  PaletteResponse,
  Slide,
} from "pages/media/types/";
import { mapAlignment } from ".";
import { fromServerAlignRatio } from "./alignRatio";

/**
 * Resolves a slide's main media kind (image vs video). `mainImage.mediaType` is
 * only set once the user replaces the media; for the original/default media the
 * type lives on the slide's `image_element` placeholder. Mirrors the resolution
 * used by the canvas (TemplateMainImage / useMainImageAssetPath).
 */
export const resolveMainImageMediaType = (
  mainImage: MainImage | undefined,
  slide: Slide | undefined
): MediaKind | undefined =>
  mainImage?.mediaType ??
  (slide && "image_element" in slide.placeholders
    ? // media_type can be null on the placeholder; normalize to undefined.
      (slide.placeholders.image_element.media_type ?? undefined)
    : undefined);

interface ProcessedTemplateAttributes {
  width: number | null;
  height: number | null;
  position: [number | string, number | string] | null;
  reference: string | null;
  animations: AnimationEffects;
  rotation: number | null;
}

export const processTemplateAttributes = (
  attributes?: TemplateItem["attributes"]
) => {
  const result: ProcessedTemplateAttributes = {
    width: null,
    height: null,
    position: null,
    reference: null,
    animations: {},
    rotation: null,
  };

  if (!attributes) return result;

  for (const attribute of attributes) {
    switch (attribute.type) {
      case "Height":
        result.height = attribute.rate;
        break;
      case "Width":
        result.width = attribute.rate;
        break;
      case "LinearRotation": {
        // Static rotation is stored as a degenerate LinearRotation (equal
        // from/to). The renderer rotates counter-clockwise, so flip the sign
        // back to the editor's clockwise convention and normalize to 0–360.
        const angle = -attribute.to_angle;
        result.rotation = Math.round(((angle % 360) + 360) % 360);
        break;
      }
      case "Position":
        if (attribute.position.type === "Align") {
          result.position = [
            attribute.position.horizontal_ratio ??
              attribute.position.horizontal ??
              0,
            attribute.position.vertical_ratio ??
              attribute.position.vertical ??
              0,
          ];
          result.reference = attribute.position.reference ?? null;
          break;
        }
        result.position = attribute.position.position;
        result.reference = attribute.position.reference ?? null;
        break;
      case "Fade": {
        const fadeDuration = attribute.duration / VIDEO_FPS;
        const fadeStartFrame =
          attribute.start.type === "Frame" ? attribute.start.frame : 0;
        // Server convention: negative startFrame anchors the animation to the
        // end of the timeline (used for exit effects). Only positive values
        // map to a user-configurable `delay`; negatives are reconstructed via
        // the `-duration` fallback in getEffectDelayFrames on save.
        const fadeConfig: AnimationConfig = {
          enabled: true,
          duration: fadeDuration,
          method: attribute.method as FadeMethod,
          ...(fadeStartFrame >= 0 && {
            delay: fadeStartFrame / VIDEO_FPS,
          }),
        };
        if (attribute.from_opacity === 0) {
          result.animations.fadeIn = fadeConfig;
          break;
        }
        result.animations.fadeOut = fadeConfig;
        break;
      }
      // LinearMove is being deprecated but we need to support it.
      case "LinearMove":
      case "Move": {
        const moveDuration = attribute.duration / VIDEO_FPS;
        const moveStart =
          attribute.type === "LinearMove"
            ? attribute.start
            : attribute.start.type === "Frame"
              ? attribute.start.frame
              : 0;
        const moveMethod =
          attribute.type === "Move"
            ? (attribute.method as MoveMethod)
            : MoveMethod.EaseInOut;
        // See Fade case above: negative moveStart is the server's "anchor
        // to end" marker for exit effects, so only positive values map to
        // a `delay`.
        const moveConfig: AnimationConfig = {
          enabled: true,
          duration: moveDuration,
          method: moveMethod,
          ...(moveStart >= 0 && {
            delay: moveStart / VIDEO_FPS,
          }),
        };

        if (
          attribute.from_position.type === "Align" &&
          attribute.to_position.type === "Align"
        ) {
          const fromHasOutside =
            attribute.from_position.vertical === "outside_top" ||
            attribute.from_position.vertical === "outside_bottom" ||
            attribute.from_position.horizontal === "outside_left" ||
            attribute.from_position.horizontal === "outside_right";
          const toHasOutside =
            attribute.to_position.vertical === "outside_top" ||
            attribute.to_position.vertical === "outside_bottom" ||
            attribute.to_position.horizontal === "outside_left" ||
            attribute.to_position.horizontal === "outside_right";

          if (fromHasOutside) {
            if (attribute.from_position.vertical === "outside_top") {
              result.animations.enterFromTop = moveConfig;
            } else if (attribute.from_position.vertical === "outside_bottom") {
              result.animations.enterFromBottom = moveConfig;
            } else if (attribute.from_position.horizontal === "outside_left") {
              result.animations.enterFromLeft = moveConfig;
            } else if (attribute.from_position.horizontal === "outside_right") {
              result.animations.enterFromRight = moveConfig;
            }
          } else if (toHasOutside) {
            if (attribute.to_position.vertical === "outside_top") {
              result.animations[Effects.leaveToTop] = moveConfig;
            } else if (attribute.to_position.vertical === "outside_bottom") {
              result.animations[Effects.leaveToBottom] = moveConfig;
            } else if (attribute.to_position.horizontal === "outside_left") {
              result.animations[Effects.leaveToLeft] = moveConfig;
            } else if (attribute.to_position.horizontal === "outside_right") {
              result.animations[Effects.leaveToRight] = moveConfig;
            }
          }

          if (attributes.every((attr) => attr.type !== "Position")) {
            if (moveStart >= 0) {
              result.position = [
                attribute.to_position.horizontal_ratio ??
                  attribute.to_position.horizontal ??
                  0,
                attribute.to_position.vertical_ratio ??
                  attribute.to_position.vertical ??
                  0,
              ];
              result.reference = attribute.to_position.reference ?? null;
            } else {
              result.position = [
                attribute.from_position.horizontal_ratio ??
                  attribute.from_position.horizontal ??
                  0,
                attribute.from_position.vertical_ratio ??
                  attribute.from_position.vertical ??
                  0,
              ];
              result.reference = attribute.from_position.reference ?? null;
            }
          }
        }
        break;
      }
    }
  }

  // Decode the renderer's far-edge Align convention for off-canvas (negative)
  // ratios back into the editor's linear top-left fraction (mirror of the
  // encode in the position attribute generators). Only negative NUMERIC ratios
  // are touched — on-canvas (>= 0) positions and keyword aligns (outside_*,
  // which deserialize to strings) pass through unchanged. Requires the box
  // size, which is known here because the whole attribute list has been read.
  if (Array.isArray(result.position)) {
    const [px, py] = result.position;
    if (typeof px === "number" && px < 0 && result.width != null) {
      result.position[0] = fromServerAlignRatio(px, result.width);
    }
    if (typeof py === "number" && py < 0 && result.height != null) {
      result.position[1] = fromServerAlignRatio(py, result.height);
    }
  }

  return result;
};

export type ProcessedTemplateItems = {
  headerRectangle: TemplateItemWithLayerIndex | null;
  headerBanner: TemplateItemWithLayerIndex | null;
  bodyRectangle: components["schemas"]["RectangleAttributes"] | null;
  bodyImage: components["schemas"]["AssetImageAttributes"] | null;
  bodyVideo: components["schemas"]["AssetVideoAttributes"] | null;
  title: TemplateItemWithLayerIndex | null;
  basicSubtitles: TemplateItemWithLayerIndex | null;
  animatedSubtitles: TemplateItemWithLayerIndex | null;
  avatarShapeItem: TemplateItem | null;
  avatarItem: TemplateItemWithLayerIndex | null;
  imageItemsArray: TemplateItemWithLayerIndex[];
  videoItemsArray: TemplateItemWithLayerIndex[];
  logo: TemplateItemWithLayerIndex | null;
  customTextItemsArray: TemplateItemWithLayerIndex[];
  shapeItemsArray: TemplateItemWithLayerIndex[];
  mainImageItem: TemplateItemWithLayerIndex | null;
  parameteredTextItemsArray: TemplateItemWithLayerIndex[];
  figureNumberItem: TemplateItemWithLayerIndex | null;
  figureUnitsItem: TemplateItemWithLayerIndex | null;
  slideLayoutShapeItemsArray: TemplateItemWithLayerIndex[];
  tableItem: TemplateItemWithLayerIndex<TableTemplateItem> | null;
};

export const processTemplateItems = (templateItems: TemplateItemWithId[]) => {
  // Create result object to hold all the categorized items
  const result: ProcessedTemplateItems = {
    headerRectangle: null,
    headerBanner: null,
    bodyRectangle: null,
    bodyImage: null,
    bodyVideo: null,
    title: null,
    basicSubtitles: null,
    animatedSubtitles: null,
    avatarShapeItem: null,
    avatarItem: null,
    imageItemsArray: [],
    videoItemsArray: [],
    logo: null,
    customTextItemsArray: [],
    shapeItemsArray: [],
    mainImageItem: null,
    parameteredTextItemsArray: [],
    figureNumberItem: null,
    figureUnitsItem: null,
    slideLayoutShapeItemsArray: [],
    tableItem: null,
  };

  let layerIndex = 1;
  templateItems.forEach((item) => {
    const { graphics, name, attributes } = item;
    const ItemWithLayerIndex: TemplateItemWithLayerIndex = {
      ...item,
      layerIndex: layerIndex,
    };

    // Check for header rectangle
    if (name === "header" && graphics.type === "Rectangle") {
      result.headerRectangle = ItemWithLayerIndex;
      layerIndex++;
    }

    // Check for header banner
    if (name === "header" && graphics.type === "AssetImage") {
      result.headerBanner = ItemWithLayerIndex;
    }

    const hasFullScreenAttribute = attributes?.some(
      (attr) => attr.type === "FullScreen"
    );

    // Check for background color rect that is not an opacity base layer
    if (
      graphics.type === "Rectangle" &&
      name !== "baseLayer" &&
      hasFullScreenAttribute
    ) {
      result.bodyRectangle = graphics;
    }

    // Check for background image
    if (graphics.type === "AssetImage" && hasFullScreenAttribute) {
      result.bodyImage = graphics;
    }

    if (graphics.type === "CustomAssetVideo") {
      if (hasFullScreenAttribute) {
        result.bodyVideo = graphics;
      } else {
        result.videoItemsArray.push(ItemWithLayerIndex);
        layerIndex++;
      }
    }

    // Collect template images
    if (
      name !== "header" &&
      name !== "logo" &&
      graphics.type === "AssetImage" &&
      !hasFullScreenAttribute
    ) {
      result.imageItemsArray.push(ItemWithLayerIndex);
      layerIndex++;
    }

    if (name === "logo" && graphics.type === "AssetImage") {
      result.logo = ItemWithLayerIndex;
      layerIndex++;
    }

    if (
      name?.includes("custom-text") ||
      (graphics.type === "Text" &&
        "content" in graphics &&
        graphics.content !== DEFAULT_TITLE_CONTENT)
    ) {
      result.customTextItemsArray.push(ItemWithLayerIndex);
      layerIndex++;
    }

    switch (graphics.type) {
      case "Text":
        if (
          "content" in graphics &&
          graphics.content === DEFAULT_TITLE_CONTENT
        ) {
          result.title = ItemWithLayerIndex;
          layerIndex++;
        }
        break;
      case "Subtitles":
        result.basicSubtitles = ItemWithLayerIndex;
        layerIndex++;
        break;
      case "RichSubtitles":
        result.animatedSubtitles = ItemWithLayerIndex;
        layerIndex++;
        break;
      case "Circle":
      case "Rectangle":
        if (name === "avatar_background") {
          result.avatarShapeItem = item;
        } else if (name?.includes("shape")) {
          result.shapeItemsArray.push(ItemWithLayerIndex);
          layerIndex++;
        }
        break;
      case "Line":
        result.shapeItemsArray.push(ItemWithLayerIndex);
        layerIndex++;
        break;
      case "Avatar":
        result.avatarItem = ItemWithLayerIndex;
        layerIndex++;
        break;
    }
  });
  return result;
};

export const processTemplateSlideItems = (templateItems: TemplateItem[]) => {
  // Create result object to hold all the categorized items
  const result: ProcessedTemplateItems = {
    headerRectangle: null,
    headerBanner: null,
    bodyRectangle: null,
    bodyImage: null,
    title: null,
    basicSubtitles: null,
    animatedSubtitles: null,
    bodyVideo: null,
    avatarShapeItem: null,
    avatarItem: null,
    imageItemsArray: [],
    videoItemsArray: [],
    logo: null,
    customTextItemsArray: [],
    shapeItemsArray: [],
    mainImageItem: null,
    parameteredTextItemsArray: [],
    figureNumberItem: null,
    figureUnitsItem: null,
    slideLayoutShapeItemsArray: [],
    tableItem: null,
  };

  let layerIndex = 1;
  templateItems.forEach((item) => {
    const { graphics, name, attributes } = item;

    const ItemWithLayerIndex: TemplateItemWithLayerIndex = {
      ...item,
      layerIndex: layerIndex,
    };

    if (name === "header") {
      if (graphics.type === "Rectangle") {
        // Check for header rectangle
        result.headerRectangle = ItemWithLayerIndex;
        layerIndex++;
      } else if (graphics.type === "AssetImage") {
        // Check for header banner
        result.headerBanner = ItemWithLayerIndex;
        layerIndex++;
      }
      return;
    }

    if (name === "logo" && graphics.type === "AssetImage") {
      result.logo = ItemWithLayerIndex;
      layerIndex++;
      return;
    }

    if (attributes?.some((attr) => attr.type === "FullScreen")) {
      if (graphics.type === "Rectangle" && name !== "baseLayer") {
        // Check for background color rect that is not an opacity base layer
        result.bodyRectangle = graphics;
      } else if (graphics.type === "AssetImage") {
        // Check for background image
        result.bodyImage = graphics;
      } else if (graphics.type === "CustomAssetVideo") {
        // Check for background video
        result.bodyVideo = graphics;
      }
      return;
    }

    if (graphics.type === "CustomAssetVideo") {
      if (name === "main_image") {
        // Main image slot whose user-replacement is a video.
        result.mainImageItem = ItemWithLayerIndex;
        layerIndex++;
        return;
      }
      result.videoItemsArray.push(ItemWithLayerIndex);
      layerIndex++;
      return;
    }

    if (graphics.type === "AssetImage") {
      if (name === "main_image") {
        // Main image slot — covers both the original theme placeholder
        // (asset_id is a parameter-ref object) and the user-replaced case
        // (asset_id is a literal string).
        result.mainImageItem = ItemWithLayerIndex;
        layerIndex++;
        return;
      }
      result.imageItemsArray.push(ItemWithLayerIndex);
      layerIndex++;
      return;
    }

    if (
      SLIDE_LAYOUT_SHAPE_NAMES.some((shapeName) => shapeName === name) ||
      SLIDE_LAYOUT_SHAPE_PARTIAL_NAMES.some((partialName) =>
        name?.includes(partialName)
      )
    ) {
      // Slide layout shapes
      result.slideLayoutShapeItemsArray.push(ItemWithLayerIndex);
      layerIndex++;
      return;
    }

    if (name?.includes("figure")) {
      result.figureNumberItem = ItemWithLayerIndex;
      layerIndex++;
      return;
    }

    switch (graphics.type) {
      case "Text":
        if (
          "content" in graphics &&
          graphics.content === DEFAULT_TITLE_CONTENT
        ) {
          result.title = ItemWithLayerIndex;
        } else {
          result.customTextItemsArray.push(ItemWithLayerIndex);
        }
        layerIndex++;
        break;
      case "Subtitles":
        result.basicSubtitles = ItemWithLayerIndex;
        layerIndex++;
        break;
      case "RichSubtitles":
        result.animatedSubtitles = ItemWithLayerIndex;
        layerIndex++;
        break;
      case "Circle":
      case "Rectangle":
        if (name === "avatar_background") {
          result.avatarShapeItem = item;
        } else if (name?.includes("shape")) {
          result.shapeItemsArray.push(ItemWithLayerIndex);
          layerIndex++;
        }
        break;
      case "Line":
        result.shapeItemsArray.push(ItemWithLayerIndex);
        layerIndex++;
        break;
      case "Avatar":
        result.avatarItem = ItemWithLayerIndex;
        layerIndex++;
        break;
      case "ParameteredText":
      case "StreamedText":
        result.parameteredTextItemsArray.push(ItemWithLayerIndex);
        layerIndex++;
        break;
      case "Switch":
        result.figureUnitsItem = ItemWithLayerIndex;
        layerIndex++;
        break;
      case "Table":
        result.tableItem = {
          ...item,
          graphics,
          layerIndex,
        };
        layerIndex++;
        break;
    }
  });
  return result;
};

export const extractTextStyleProperties = (
  item: TemplateItem["graphics"]
): {
  wrapText?: boolean;
  adjustFont?: boolean;
  upperCase?: boolean;
  verticalAlign?: "top" | "center" | "bottom";
} => ({
  wrapText: "wrap_text" in item ? item.wrap_text : undefined,
  adjustFont: "adjust_font" in item ? item.adjust_font : undefined,
  upperCase: "upper_case" in item ? item.upper_case : undefined,
  verticalAlign: "vertical_align" in item ? item.vertical_align : undefined,
});

const textTypes = new Set([
  "Text",
  "Subtitles",
  "RichSubtitles",
  "ParameteredText",
  "StreamedText",
]);

interface TextColorProperties {
  color?: string;
  textBackground?: string;
  strokeColor?: string;
}

const checkTextColors = (
  item: TemplateItem["graphics"]
): TextColorProperties => {
  const result: TextColorProperties = {};

  const textSource = "text" in item ? item.text : item;

  if ("color" in textSource) {
    const color = mapServerColor(textSource.color);
    if (!isGradient(color)) {
      result.color = color;
    }
  }

  if ("background" in textSource && textSource.background) {
    const textBackground = mapServerColor(textSource.background);
    if (!isGradient(textBackground)) {
      result.textBackground = textBackground;
    }
  }

  if ("stroke_color" in textSource && textSource.stroke_color) {
    const strokeColor = mapServerColor(textSource.stroke_color);
    if (!isGradient(strokeColor)) {
      result.strokeColor = strokeColor;
    }
  }

  return result;
};

export const extractTextProperties = (
  item?: TemplateItem["graphics"]
): {
  fontAssetId?: string;
  fontSize?: number;
  color?: string;
  textBackground?: string;
  content?: string;
  strokeColor?: string;
  lineHeight?: number;
  textAlign?: HorizontalTextAlign;
  index?: number;
  path?: string;
} | null => {
  if (!item || !textTypes.has(item.type)) return null;
  const { color, textBackground, strokeColor } = checkTextColors(item);

  if (item.type === "Text") {
    return {
      fontAssetId: item.font,
      fontSize: item.size,
      color,
      textBackground,
      content: item.content,
      strokeColor,
      lineHeight: item.line_spacing,
    };
  }

  if (item.type === "Subtitles") {
    return {
      fontAssetId: item.font,
      fontSize: item.size,
      color,
      textBackground,
    };
  }

  if (item.type === "RichSubtitles") {
    return {
      fontAssetId: item.text.font,
      fontSize: item.text.size,
      color,
      strokeColor,
      textBackground,
      lineHeight: item.line_spacing,
    };
  }

  if (item.type === "ParameteredText" || item.type === "StreamedText") {
    const { path, index } =
      item.type === "ParameteredText"
        ? getParameteredTextPath(item)
        : getStreamedTextPath(item);

    if (!path) return null;

    return {
      fontAssetId: item.font,
      fontSize: item.size,
      color,
      textBackground,
      strokeColor,
      lineHeight: item.line_spacing,
      textAlign: item.align,
      path,
      index,
    };
  }

  return null;
};

const getParameteredTextPath = (
  item: Extract<TemplateItem["graphics"], { type: "ParameteredText" }>
): { path?: string; index?: number } => {
  if ("path" in item.parameters.content) {
    return {
      path: item.parameters.content.path,
      index: item.parameters.content.index ?? undefined,
    };
  }
  return { path: undefined, index: undefined };
};

const getStreamedTextPath = (
  item: Extract<TemplateItem["graphics"], { type: "StreamedText" }>
): { path?: string; index?: number } => {
  if (typeof item.content === "object" && "path" in item.content.content) {
    return {
      path: item.content.content.path,
      index: item.content.content.index ?? undefined,
    };
  }
  return { path: undefined, index: undefined };
};

export const getAvatarImagePath = (presenter: Avatar) => {
  return presenter.avatarShot === "CloseUp"
    ? presenter.presenter?.face_profile?.thumbnail_asset_path
    : (presenter.presenter?.face_profile?.extracted_asset_path ??
        presenter.presenter?.face_profile?.custom_asset_path);
};

export const getTextAlign = (textItem?: TemplateItem["graphics"]) => {
  if (textItem?.type === "Text") return textItem.align;
  return "center";
};

export const getVerticalAlign = (textItem?: TemplateItem["graphics"]) => {
  if (textItem?.type === "Text") return textItem.vertical_align;
  return "top";
};
export const getAvatarShapeBackgroundColor = (
  graphics?: TemplateItem["graphics"]
) => {
  const isValidShape =
    graphics?.type === "Circle" || graphics?.type === "Rectangle";
  if (!isValidShape) return undefined;
  const color = mapServerColor(graphics.color);
  if (color === NULL_COLOR) return undefined;
  return color;
};

type ValidShapeGraphics =
  | components["schemas"]["RectangleAttributes"]
  | components["schemas"]["CircleAttributes"]
  | components["schemas"]["LineAttributes"];

export const isValidShapeGraphics = (
  graphics: TemplateItem["graphics"]
): graphics is ValidShapeGraphics => {
  return (
    graphics.type === "Rectangle" ||
    graphics.type === "Circle" ||
    graphics.type === "Line"
  );
};

export const isLineShape = (
  graphics: TemplateItem["graphics"]
): graphics is components["schemas"]["LineAttributes"] => {
  return graphics.type === "Line";
};

export const getShapeType = (
  graphicsType: ValidShapeGraphics["type"]
): ShapeType => {
  if (graphicsType === "Circle") return "circle";
  if (graphicsType === "Line") return "line";
  return "rectangle";
};

export const getLineOrientation = (
  graphics: TemplateItem["graphics"]
): LineOrientation | undefined => {
  if (graphics.type === "Line") return graphics.orientation;
  return undefined;
};

export const getLineSize = (
  lineItem: TemplateItem,
  orientation: LineOrientation
): { width: number; height: number } | undefined => {
  const { graphics, attributes } = lineItem;
  const { width, height } = processTemplateAttributes(attributes);
  if (graphics.type === "Line" && width && height) {
    return {
      width:
        orientation === LINE_ORIENTATIONS.HORIZONTAL
          ? width
          : graphics.thickness,
      height:
        orientation === LINE_ORIENTATIONS.HORIZONTAL
          ? graphics.thickness
          : height,
    };
  }
  return undefined;
};

export const duplicateItem = <T extends MediaItem | CustomText | Shape>(
  item: T,
  addFunction: (item: T) => void,
  previewWidth: number,
  previewHeight: number,
  newHighestLayerIndex: number,
  id?: string
) => {
  const offsetX = 10 / previewWidth;
  const offsetY = 10 / previewHeight;

  let newX = item.position.x + offsetX;
  let newY = item.position.y + offsetY;

  // Wrap to 0 if position + size would exceed preview boundaries
  if (newX + item.size.width > 1) {
    newX = 1 - item.size.width;
  }
  if (newY + item.size.height > 1) {
    newY = 1 - item.size.height;
  }

  const position = {
    x: newX,
    y: newY,
  };
  const duplicatedItemId = id ?? nanoid();
  const duplicatedItem = {
    ...item,
    id: duplicatedItemId,
    position,
    layerIndex: newHighestLayerIndex,
  };
  addFunction(duplicatedItem);
  return duplicatedItem;
};

// The single source of truth for "does this presenter actually render an avatar on screen".
export const hasVisibleAvatar = (presenter: Avatar): boolean =>
  !!(
    presenter.presenter?.face_profile &&
    presenter.size.width &&
    presenter.size.height
  );

export const generatePresenterItems = (
  presenter: Avatar,
  aspectRatio: number,
  detectCenteredPositions = false
): ItemWithLayerIndex => {
  const presenterItems: TemplateItem[] = [];
  if (hasVisibleAvatar(presenter)) {
    const avatarItem: TemplateItem = generateAvatarItem(presenter, aspectRatio);

    const shapeItem: TemplateItem = generateAvatarShapeItem(
      presenter,
      aspectRatio,
      detectCenteredPositions
    );
    presenterItems.push(shapeItem, avatarItem);
  }
  return { item: presenterItems, layerIndex: presenter.layerIndex };
};

export const findItemByTypeAndId = (
  item: ItemType,
  templateState: TemplateState
): CopyPasteItem | undefined => {
  switch (item.type) {
    case ITEM_TYPES.CustomText: {
      const customText = [
        ...templateState.customTexts,
        ...(templateState.parameteredTexts ?? []),
        ...(templateState.figure?.number ? [templateState.figure.number] : []),
        ...(templateState.figure?.units ? [templateState.figure.units] : []),
      ].find((text) => text.id === item.id);
      return (
        customText && {
          ...customText,
          size: {
            width: customText.size.width,
            height: customText.size.height ?? 0,
          },
          type: ITEM_TYPES.CustomText,
        }
      );
    }
    case ITEM_TYPES.Shape: {
      const shape =
        templateState.shapes.find((shape) => shape.id === item.id) ??
        templateState.slideLayoutShapes?.find((shape) => shape.id === item.id);
      return (
        shape && {
          ...shape,
          type: ITEM_TYPES.Shape,
        }
      );
    }
    case ITEM_TYPES.Image: {
      const image = [
        ...templateState.images,
        ...(templateState.mainImage ? [templateState.mainImage] : []),
      ].find((image) => image.id === item.id);
      return (
        image && {
          ...image,
          type: ITEM_TYPES.Image,
        }
      );
    }
    case ITEM_TYPES.Video: {
      const video = templateState.videos.find((video) => video.id === item.id);
      return (
        video && {
          ...video,
          type: ITEM_TYPES.Video,
        }
      );
    }
    case ITEM_TYPES.Logo: {
      return {
        ...templateState.logo,
        type: ITEM_TYPES.Logo,
      };
    }
    default:
      return undefined;
  }
};

export const roundToHalfPixel = (number: number) => {
  // Preserve sign: positions can be negative (e.g. an item dragged partly past
  // the top/left edge, common for a rotated item near the canvas edge). Using
  // Math.abs here flipped negatives to positive, so the committed position
  // (sign-preserving via getRelativePosition) no longer matched the rendered
  // pixels — the item jumped on the next render.
  return Math.round(number * 2) / 2;
};

export const mapRelativeToPixels = (relative: number, relativeTo: number) =>
  roundToHalfPixel(relative * relativeTo);

export const getAvatarWidthForGeneration = (
  presenter: Avatar,
  aspectRatio?: number
) => {
  if (presenter.avatarShape === AvatarShape.Circle) {
    return undefined;
  }
  if (presenter.avatarShape === AvatarShape.Box && aspectRatio) {
    return presenter.size.height / aspectRatio;
  }
  return presenter.size.width;
};

export const isPlaceholderValue = (
  value: string | [number, number, number, number] | undefined
): value is string => {
  return (
    typeof value === "string" && value.startsWith("{") && value.endsWith("}")
  );
};

export function mapServerColor(
  color: string | [number, number, number, number] | GradientDefinition
): string | GradientDefinition {
  if (isGradient(color)) {
    return color;
  }
  if (isPlaceholderValue(color)) {
    return color;
  }
  return rgbaToHex(color);
}

export const mapToServerColor = (color: string) => {
  if (isPlaceholderValue(color)) {
    return color;
  }
  return hexToRgba(color);
};

export const getResolvedColorFromBrandkitPalette = (
  placeholder: string,
  brandKitPalette: PaletteResponse | undefined
) => {
  if (!brandKitPalette) {
    console.error("Brand kit palette is undefined");
    return DEFAULT_TEXT_COLOR;
  }
  const paletteColor = placeholder.split(".").at(-1)?.replace("}", "");
  if (!paletteColor || !(paletteColor in brandKitPalette)) {
    console.error(
      `Color ${paletteColor} not found in brandkit palette`,
      brandKitPalette
    );
    return DEFAULT_TEXT_COLOR;
  }
  return (
    brandKitPalette[paletteColor as keyof typeof brandKitPalette] ?? NULL_COLOR
  );
};

export const resolveColorToBrandkitColorIfNeeded = <
  T extends string | GradientDefinition | undefined,
>(
  color: T,
  brandKitPalette: PaletteResponse | undefined
): T => {
  if (isGradient(color)) return color;
  return (
    isPlaceholderValue(color)
      ? getResolvedColorFromBrandkitPalette(color, brandKitPalette)
      : color
  ) as T;
};

export const resolveToBrandkitFontIfNeeded = (
  value: string | undefined,
  brandKitTitleFont: BrandKitAsset | undefined,
  brandKitSubtitlesFont: BrandKitAsset | undefined,
  brandKitTextFont: BrandKitAsset | undefined
) => {
  if (isPlaceholderValue(value)) {
    if (value.includes("title_font")) {
      return brandKitTitleFont?.asset_id;
    }
    if (value.includes("subtitles_font")) {
      return brandKitSubtitlesFont?.asset_id;
    }
    if (value.includes("text_font")) {
      return brandKitTextFont?.asset_id;
    }
  }
  return value;
};

export const getSlotsForSlideTypeFromTheme = (
  theme: components["schemas"]["ThemeResponse"],
  slideLayoutType: string
): Slot[] | undefined => {
  const layout = theme.layouts[slideLayoutType];
  if (!layout) return [];

  const {
    parameteredTextItemsArray,
    mainImageItem,
    figureNumberItem,
    figureUnitsItem,
    tableItem,
  } = processTemplateSlideItems(layout.layout);

  const slots: Slot[] = [];
  const seenPaths = new Set<string>();
  for (const item of parameteredTextItemsArray) {
    const props = extractTextProperties(item.graphics);
    if (!props?.path || seenPaths.has(props.path)) continue;
    seenPaths.add(props.path);
    slots.push({
      path: props.path,
      parameteredTextItem: item,
    });
  }

  if (mainImageItem) slots.push({ mainImageItem });
  if (figureNumberItem)
    slots.push({
      figureNumberItem,
      figureUnitsItem: figureUnitsItem ?? undefined,
    });
  if (tableItem) slots.push({ tableItem });

  return slots.length > 0 ? slots : undefined;
};

export const resolveParameteredTextPathToSlidePlaceholder = (
  path: string,
  slide: Slide,
  index?: number
) => {
  switch (path) {
    case "title":
      return "title" in slide.placeholders
        ? slide.placeholders.title
        : undefined;
    case "subtitle":
      return "subtitle" in slide.placeholders
        ? (slide.placeholders.subtitle ?? undefined)
        : undefined;
    case "text":
      return "text" in slide.placeholders ? slide.placeholders.text : undefined;
    case "paragraph_1":
      return "paragraph_1" in slide.placeholders
        ? slide.placeholders.paragraph_1
        : undefined;
    case "paragraph_2":
      return "paragraph_2" in slide.placeholders
        ? slide.placeholders.paragraph_2
        : undefined;
    case "short_description":
      return "figure" in slide.placeholders
        ? slide.placeholders.figure.short_description
        : undefined;
    case "quote":
      return "quote" in slide.placeholders
        ? slide.placeholders.quote
        : undefined;
    case "bullet_points":
      return "bullet_points" in slide.placeholders && index !== undefined
        ? slide.placeholders.bullet_points[index]
        : undefined;
    case "figure":
      return "figure" in slide.placeholders
        ? slide.placeholders.figure.figure.toString()
        : undefined;
    case "unit":
      return "figure" in slide.placeholders
        ? slide.placeholders.figure.unit
        : undefined;
    default:
      return;
  }
};

const hasPlaceholderChanged = (
  content: string | undefined,
  path: string,
  slide: Slide,
  index?: number
): boolean => {
  if (!content) return false;
  const current = resolveParameteredTextPathToSlidePlaceholder(
    path,
    slide,
    index
  );
  return current === undefined || String(current) !== content;
};

/**
 * Checks if any parameterized text content has changed compared to the original
 * slide placeholders. Used to determine whether a slides asset save is needed.
 */
export const hasParameteredTextContentChanged = (
  parameteredTexts: ParameteredText[] | undefined,
  figure: Figure | undefined,
  slide: Slide
): boolean => {
  const hasTextChanged = parameteredTexts?.some((text) =>
    hasPlaceholderChanged(text.content, text.path, slide, text.index)
  );
  if (hasTextChanged) return true;

  if (!figure) return false;

  return (
    hasPlaceholderChanged(figure.number.content, "figure", slide) ||
    hasPlaceholderChanged(figure.units?.content, "unit", slide)
  );
};

export const syncParameteredTextContentToSlidePlaceholders = (
  parameteredTexts: ParameteredText[] | undefined,
  figure: Figure | undefined,
  slide: Slide
): Slide => {
  const updatedPlaceholders: Slide["placeholders"] = { ...slide.placeholders };

  if (parameteredTexts) {
    for (const text of parameteredTexts) {
      if (!text.content) continue;
      // Sanitize any text before it is persisted to the slide asset.
      const content = sanitizeText(text.content);
      switch (text.path) {
        case "title":
          if ("title" in updatedPlaceholders) {
            updatedPlaceholders.title = content;
          }
          break;
        case "subtitle":
          if ("subtitle" in updatedPlaceholders) {
            updatedPlaceholders.subtitle = content;
          }
          break;
        case "text":
          if ("text" in updatedPlaceholders) {
            updatedPlaceholders.text = content;
          }
          break;
        case "paragraph_1":
          if ("paragraph_1" in updatedPlaceholders) {
            updatedPlaceholders.paragraph_1 = content;
          }
          break;
        case "paragraph_2":
          if ("paragraph_2" in updatedPlaceholders) {
            updatedPlaceholders.paragraph_2 = content;
          }
          break;
        case "quote":
          if ("quote" in updatedPlaceholders) {
            updatedPlaceholders.quote = content;
          }
          break;
        case "bullet_points":
          if (
            "bullet_points" in updatedPlaceholders &&
            text.index !== undefined
          ) {
            const bulletPoints = [...updatedPlaceholders.bullet_points];
            bulletPoints[text.index] = content;
            updatedPlaceholders.bullet_points = bulletPoints;
          }
          break;
      }
    }
  }

  if (figure) {
    if (figure.number.content && "figure" in updatedPlaceholders) {
      updatedPlaceholders.figure = {
        ...updatedPlaceholders.figure,
        figure: sanitizeText(figure.number.content),
      };
    }

    if (figure.units) {
      const unit = figure.units;
      if (unit.content && "figure" in updatedPlaceholders) {
        if (unit.path === "unit") {
          updatedPlaceholders.figure = {
            ...updatedPlaceholders.figure,
            unit: sanitizeText(unit.content),
          };
        }
      }
    }
  }

  return {
    ...slide,
    placeholders: updatedPlaceholders,
  } as Slide;
};

type ReferencedItemAttributes = {
  position: { x: number; y: number };
  width: number;
  height: number;
};

export const generateGetPositionFn =
  (templateItems: TemplateItem[]) =>
  (
    itemPosition: [string | number, string | number],
    reference: string | null,
    itemWidth: number,
    itemHeight: number | null,
    itemType: string
  ) => {
    const referencedItemAttributes: ReferencedItemAttributes = {
      position: { x: 0, y: 0 },
      width: 1,
      height: 1,
    };
    if (reference) {
      const referencedItem = templateItems.find(
        (item) => item.name === reference
      );
      if (referencedItem) {
        const {
          position: referencedItemPosition,
          width,
          height,
        } = processTemplateAttributes(referencedItem.attributes);
        if (referencedItemPosition && width && height) {
          referencedItemAttributes.position.x =
            typeof referencedItemPosition[0] === "string"
              ? mapAlignment(
                  referencedItemPosition[0],
                  width,
                  `reference item of ${itemType}`
                )
              : referencedItemPosition[0];
          referencedItemAttributes.position.y =
            typeof referencedItemPosition[1] === "string"
              ? mapAlignment(
                  referencedItemPosition[1],
                  height,
                  `reference item of ${itemType}`
                )
              : referencedItemPosition[1];
          referencedItemAttributes.width = width;
          referencedItemAttributes.height = height;
        }
      }
    }

    const x =
      typeof itemPosition[0] === "string"
        ? mapAlignment(
            itemPosition[0],
            itemWidth,
            itemType,
            referencedItemAttributes.position.x,
            referencedItemAttributes.width
          )
        : itemPosition[0] * referencedItemAttributes.width;
    const y =
      typeof itemPosition[1] === "string"
        ? mapAlignment(
            itemPosition[1],
            itemHeight,
            itemType,
            referencedItemAttributes.position.y,
            referencedItemAttributes.height
          )
        : itemPosition[1] * referencedItemAttributes.height;

    return {
      x,
      y,
    };
  };

export const BRANDKIT_FONT_PLACEHOLDERS = {
  Title: TITLE_FONT_PLACEHOLDER,
  Subtitles: SUBTITLES_FONT_PLACEHOLDER,
  Text: TEXT_FONT_PLACEHOLDER,
} as const;

export type FontPurpose = "Title" | "Subtitles" | "Text";

export const BRANDKIT_COLOR_PLACEHOLDERS = {
  primary_color: PRIMARY_COLOR_PLACEHOLDER,
  secondary_color: SECONDARY_COLOR_PLACEHOLDER,
  title_color: TITLE_COLOR_PLACEHOLDER,
  subtitle_color: SUBTITLE_COLOR_PLACEHOLDER,
} as const;

export type ColorKey = keyof typeof BRANDKIT_COLOR_PLACEHOLDERS;

const isBrandkitFontPurpose = (purpose: string): purpose is FontPurpose => {
  return purpose in BRANDKIT_FONT_PLACEHOLDERS;
};

const isBrandkitColorKey = (key: string): key is ColorKey => {
  return key in BRANDKIT_COLOR_PLACEHOLDERS;
};

export const resolveFontAssetIdToBrandKitPlaceholder = (
  assetId: string | undefined,
  brandKitFonts: BrandKitAsset[]
) => {
  if (!assetId) return;
  const brandkitFont = brandKitFonts.find((f) => f.asset_id === assetId);
  if (brandkitFont?.purpose && isBrandkitFontPurpose(brandkitFont.purpose)) {
    return BRANDKIT_FONT_PLACEHOLDERS[brandkitFont.purpose];
  }
  return assetId;
};

const normalizeHexForComparison = (hex: string): string => {
  const lower = hex.toLowerCase();
  if (lower.length === 9 && lower.endsWith("ff")) {
    return lower.slice(0, 7);
  }
  return lower;
};

export const resolveColorToBrandKitPlaceholder = (
  color: string | undefined,
  brandKitPalette: PaletteResponse | undefined
) => {
  if (!color) return DEFAULT_TEXT_COLOR;
  let returnValue = color;
  if (!brandKitPalette) {
    console.error("Brand kit palette is undefined");
    return DEFAULT_TEXT_COLOR;
  }
  Object.entries(brandKitPalette).forEach(([key, value]) => {
    if (
      value &&
      normalizeHexForComparison(value) === normalizeHexForComparison(color) &&
      isBrandkitColorKey(key)
    ) {
      returnValue = BRANDKIT_COLOR_PLACEHOLDERS[key];
    }
  });
  return returnValue;
};

export const BRANDKIT_VIDEO_PLACEHOLDERS = {
  Introduction: INTRODUCTION_VIDEO_PLACEHOLDER,
  Closing: CLOSING_VIDEO_PLACEHOLDER,
} as const;

export type VideoPurpose = "Introduction" | "Closing";

const isBrandkitVideoPurpose = (purpose: string): purpose is VideoPurpose => {
  return purpose in BRANDKIT_VIDEO_PLACEHOLDERS;
};

/**
 * Resolves a video placeholder to the actual brand kit asset ID.
 * Used when displaying or generating templates.
 */
export const resolveToBrandkitVideoIfNeeded = (
  value: string | undefined,
  brandKitIntro: BrandKitAsset | undefined,
  brandKitClosing: BrandKitAsset | undefined
): string | undefined => {
  if (isPlaceholderValue(value)) {
    if (value === INTRODUCTION_VIDEO_PLACEHOLDER) {
      return brandKitIntro?.asset_id;
    }
    if (value === CLOSING_VIDEO_PLACEHOLDER) {
      return brandKitClosing?.asset_id;
    }
  }
  return value;
};

/**
 * Converts an asset ID to a brand kit placeholder if it matches a brand kit intro/closing video.
 * Used when saving templates.
 * Note: BK intro/closing can only be videos, so only Video type assets are considered.
 */
export const resolveVideoAssetIdToBrandKitPlaceholder = (
  assetId: string | undefined,
  brandKitIntro: BrandKitAsset | undefined,
  brandKitClosing: BrandKitAsset | undefined
): string | undefined => {
  if (!assetId) return undefined;

  // Check if the asset matches the brand kit intro (must be a video)
  if (
    brandKitIntro?.asset_id === assetId &&
    brandKitIntro.type === "Video" &&
    isBrandkitVideoPurpose(brandKitIntro.purpose)
  ) {
    return BRANDKIT_VIDEO_PLACEHOLDERS[brandKitIntro.purpose];
  }

  // Check if the asset matches the brand kit closing (must be a video)
  if (
    brandKitClosing?.asset_id === assetId &&
    brandKitClosing.type === "Video" &&
    isBrandkitVideoPurpose(brandKitClosing.purpose)
  ) {
    return BRANDKIT_VIDEO_PLACEHOLDERS[brandKitClosing.purpose];
  }

  return assetId;
};

export const getIntroDuration = (duration: number | undefined) => {
  if (!duration) return INTRODUCTION_DURATION_PLACEHOLDER;
  return Math.min(duration, MAX_INTRODUCTION_DURATION_IN_FRAMES);
};

export const getClosingDuration = (duration: number | undefined) => {
  if (!duration) return CLOSING_DURATION_PLACEHOLDER;
  return Math.min(duration, MAX_CLOSING_DURATION_IN_FRAMES);
};

export const getMaxDurationInFrames = (type: TemplateType): number => {
  if (type === TemplateType.INTRODUCTION)
    return MAX_INTRODUCTION_DURATION_IN_FRAMES;
  if (type === TemplateType.CLOSING) return MAX_CLOSING_DURATION_IN_FRAMES;
  return MAX_TRANSITION_DURATION_IN_FRAMES;
};

export const getDisplayedDurationInFrames = (
  item: VideoScene,
  type: TemplateType
): number | undefined => {
  if (item.duration !== undefined) return item.duration;
  if (item.assetDuration !== undefined) return item.assetDuration;
  if (type === TemplateType.TRANSITION)
    return DEFAULT_TRANSITION_DURATION_IN_FRAMES;
  return undefined;
};

export const parseSpeechDuration = (
  template: components["schemas"]["MediaTemplate"] | undefined
): number | undefined => {
  const speech = (
    template as { speech?: { duration?: number | string } } | undefined
  )?.speech;
  if (typeof speech?.duration === "number") return speech.duration;
  return undefined;
};

export const isGradient = (
  background?: string | [number, number, number, number] | GradientDefinition
): background is GradientDefinition =>
  typeof background === "object" &&
  "geometry" in background &&
  "stops" in background;

const transformGradientDefinitionColorToRgba = (
  color: GradientDefinition["stops"][number]["color"]
): string => {
  return typeof color === "string"
    ? color
    : `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`;
};

const gradientToCss = (gradient: GradientDefinition): string => {
  const { start, end } = gradient.geometry;
  const angle =
    Math.atan2(end[1] - start[1], end[0] - start[0]) * (180 / Math.PI) + 90;
  if (gradient.stops.length === 0) return NULL_COLOR;
  if (gradient.stops.length === 1 && gradient.stops[0]?.color)
    return transformGradientDefinitionColorToRgba(gradient.stops[0].color);
  const stops = gradient.stops
    .map((s) => {
      const color = transformGradientDefinitionColorToRgba(s.color);
      return `${color} ${(s.position * 100).toFixed(1)}%`;
    })
    .join(", ");
  return `linear-gradient(${angle.toFixed(1)}deg, ${stops})`;
};

export const getBackgroundStyle = (
  background: string | GradientDefinition,
  brandkitPalette: BrandKit["palette"] | undefined
): React.CSSProperties => {
  if (isGradient(background)) {
    return { background: gradientToCss(background) };
  }
  return {
    backgroundColor: resolveColorToBrandkitColorIfNeeded(
      background,
      brandkitPalette
    ),
  };
};

export const normalizeGradientForColorPicker = (
  background: string | GradientDefinition | undefined,
  brandkitPalette: PaletteResponse | undefined
): string | { color: string; percent: number }[] | undefined => {
  if (!background) return undefined;
  if (isGradient(background)) {
    return background.stops.map((s) => ({
      color: rgbaToHex(s.color),
      percent: s.position * 100,
    }));
  }
  return resolveColorToBrandkitColorIfNeeded(background, brandkitPalette);
};
